import { faArrowRotateLeft, faLeftFromBracket } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from 'uuid';
import cx from 'classnames';
import _startCase from 'lodash/startCase';

import { useRect } from "../../../hooks/useRect";
import { mapRange } from "../../../utils";
import { addMarker, groupedUndo, markersSelector, setSpaceMarker, SpaceType, updateMarker, useAppDispatch, useAppSelector } from "../../../store";
import { useRootElementEvents } from "../../../hooks/useRootElementEvents";

import {
  svgRef,
  markers,
  markerStepContent,
  markerConfigurationComponents,
  markerThumbnailComponents,
  useMarkerReactiveState,
  useControlPointIndicatorRefs,
  useSyncSnappingControlPointsState,
  StateType,
  Markers,
  StepsType,
  disabledStepCombinations,
} from "./markerParts";
import { MarkerPart, Point } from "./markerParts/shared";
import { serializeMarker, deserializeMarker } from './markerParts/shared/serialization';
import { useUpdateDecorationSnapCenter } from "./useUpdateDecorationSnapCenter";

import './MarkerEditor.scss';

const MARKER_EDITING_CIRCLE_RADIUS = 0.0375;
const MARKER_EDITING_CROSS_RADIUS = 0.0625;
const CONTROL_POINT_INDICATOR_STROKE_WIDTH = 0.00625;

export const defaultActiveState = { shape: 'teardrop', decoration: 'customIcon', shapeBorder: 'solid', shapeBorderColor: 'solid', decorationBorder: 'none', decorationBorderColor: 'solid', shapeFill: 'solid', decorationFill: 'solid', decorationFont: 'font' } as const;

export function MarkerEditor({ id, space, isNewMarker, onClose }: { id: string, space: SpaceType, isNewMarker: boolean, onClose: () => void }) {
  const dispatch = useAppDispatch();
  const storeMarkers = useAppSelector(markersSelector);
  const existingMarker = useMemo(() => storeMarkers.find((m) => m.id === id), [storeMarkers, id]);
  const deserializedActiveState = useMemo(() => deserializeMarker(existingMarker), [existingMarker]);

  // Capture initial state when component mounts
  const initialState = useMemo<StateType>(() => ({
    active: { ...defaultActiveState, ...(deserializedActiveState ?? {}) },
    step: 'shape',
    draggedControlPoint: undefined,
    snappingDisabled: false,
    previousDecorationSnapCenter: null,
  }), [deserializedActiveState]);

  // Capture initial marker parts state when component mounts
  const initialMarkerPartsState = useMemo(() => {
    const snapshot: Record<string, Record<string, any>> = {};
    Object.entries(markers).forEach(([stepName, stepMarkers]) => {
      snapshot[stepName] = {};
      Object.entries(stepMarkers).forEach(([partName, part]) => {
        snapshot[stepName][partName] = {
          controlPoints: Object.fromEntries(
            (Object.entries(part.controlPoints) as [string, Point][])
              .map(([cpName, point]) => [
                cpName,
                new Point(point.x, point.y)
              ])
          ),
          reactiveState: Object.fromEntries(
            Object.entries(part.reactiveState.internal).filter(
              ([key]) => !Object.keys(part.reactiveStateStoreHandler).includes(key)
            )
          ),
        };
      });
    });
    return snapshot;
  }, []);

  const [state, setState] = useState<StateType>(initialState);

  const reactiveState = useMarkerReactiveState();

  const { rect: svgRect, measureRef: measureSvgRef } = useRect(svgRef, [state]);

  const controlPointIndicatorRefs = useControlPointIndicatorRefs();

  const activePart = useMemo(
    () => (markers[state.step] as any)[state.active[state.step]] as MarkerPart,
    [state],
  );
  const activeControlPointIndicatorRefs = useMemo(
    () => controlPointIndicatorRefs[state.step][state.active[state.step]],
    [state, controlPointIndicatorRefs],
  );

  const updateAll = useCallback(() => {
    Object.entries(markers)
      .forEach(([step, options]) => {
        Object.entries(options)
          .forEach(([name, part]) => {
            (Object.entries(part.controlPoints) as [string, Point][])
              .forEach(([cpName, cp]) => {
                (['user', 'constrained'] as const)
                  .forEach((type) => {
                    controlPointIndicatorRefs[step][name][cpName][type].current?.style.setProperty('translate', cp.toCSSTranslate());
                  });
              });
          });
      });

    Object.entries(state.active)
      .forEach(([step, partName]) => {
        markerStepContent[step].update(
          ((markers[step as keyof Markers] as any)[partName] as MarkerPart).Content({ id, space, extraProps: {} }),
        );
      });
  }, [state, controlPointIndicatorRefs, id, space]);

  // Ensure Content is updated on the first render, when regular state changes, or when the reactive getReactiveStateSnapshot state changes
  useEffect(updateAll, [state, reactiveState, updateAll]);

  // Auto-move decoration to shape center when shape changes, unless user already moved it
  useUpdateDecorationSnapCenter(state, setState, updateAll);

  // Because we bypass React, to reset them we must also do so manually, unfortunately
  const reset = useCallback(() => {
    // Restore all marker parts to their initial state
    Object.entries(initialMarkerPartsState).forEach(([stepName, stepMarkers]) => {
      Object.entries(stepMarkers).forEach(([partName, partState]) => {
        const part = (markers as any)[stepName][partName];

        // Restore control points
        (Object.entries(partState.controlPoints) as [string, Point][]).forEach(([cpName, point]) => {
          if (part.controlPoints[cpName]) {
            part.controlPoints[cpName].x = point.x;
            part.controlPoints[cpName].y = point.y;
          }
        });

        // Restore internal reactive state (excluding store-derived properties)
        Object.entries(partState.reactiveState).forEach(([key, value]) => {
          part.reactiveState.internal[key] = value;
        });
      });
    });

    setState(initialState);
  }, [initialState, initialMarkerPartsState]);

  useSyncSnappingControlPointsState(state);

  useRootElementEvents(
    ['keydown', 'keyup'],
    useCallback(
      (e: KeyboardEvent) => {
        if (state.draggedControlPoint && e.key === 'Shift') {
          e.preventDefault();
          e.stopPropagation();

          setState((prev) => ({ ...prev, snappingDisabled: e.shiftKey }));
        }
      },
      [state.draggedControlPoint],
    ),
  );

  const saveAndClose = useCallback(() => {
    groupedUndo.batch(() => {
      const base64 = serializeMarker(state, id);
      const decorationId = markers.decoration[state.active.decoration]
        .reactiveState.internal.activeDecoration;
      const json = JSON.stringify(
        {
          active: state.active,
          parts: Object.entries(state.active).map(([step, partName]) => {
            const part = (markers as any)[step][partName];
            return JSON.stringify(part.serializedState);
          }),
        }
      );

      if (isNewMarker) {
        const id = uuidv4();
        dispatch(setSpaceMarker({ spaceId: space.id, markerId: id }));
        dispatch(addMarker({
          id,
          base64,
          size: '57x57',
          kind: 'editor',
          decorationId,
          state: json,
        }));
      } else {
        dispatch(updateMarker({
          id,
          base64,
          decorationId,
          state: json,
        }));
      }
    });
    onClose();
  }, [onClose, state, id, space, dispatch, isNewMarker]);

  return (
    <dialog
      open
      id="marker-editor"
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div className="wrapper">
        <article>
          <header>
            <button title="Go back" onClick={saveAndClose}>
              <FontAwesomeIcon
                icon={faLeftFromBracket}
                color="white"
              />
            </button>
            <button title="Reset" onClick={reset}>
              <FontAwesomeIcon
                icon={faArrowRotateLeft}
                color="white"
              />
            </button>
          </header>
          <section className="marker-preview">
            {activePart && (
              <svg
                id={`marker-${id}`}
                viewBox="-1 -1 2 2"
                ref={measureSvgRef}
                style={{ position: 'relative' }}
                xmlns="http://www.w3.org/2000/svg"
                onMouseLeave={() => {
                  if (!state.draggedControlPoint) return;

                  activeControlPointIndicatorRefs[state.draggedControlPoint]['user'].current!
                    .style.setProperty('translate', activePart.controlPoints[state.draggedControlPoint].toCSSTranslate());
                }}
                onMouseMove={(e) => {
                  if (!state.draggedControlPoint) return;

                  e.stopPropagation();
                  e.preventDefault();

                  const newPoint = new Point(
                    mapRange(e.pageX, svgRect!.left, svgRect!.right, -1, 1),
                    mapRange(e.pageY, svgRect!.top, svgRect!.bottom, -1, 1),
                  );
                  activePart.controlPoints[state.draggedControlPoint] = newPoint;

                  updateAll();

                  // Update the dragged control point indicator to the position the user dragged to
                  activeControlPointIndicatorRefs[state.draggedControlPoint]['user'].current!
                    .style.setProperty('translate', newPoint.toCSSTranslate());
                }}
                onMouseUp={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setState((prev) => ({ ...prev, draggedControlPoint: undefined, previousDecorationSnapCenter: null }));
                }}
              >
                {Object.values(markerStepContent)}

                {Object.keys(activePart.controlPoints)
                  .map((controlPointName) => (
                    <g key={controlPointName}>
                      <g
                        ref={activeControlPointIndicatorRefs[controlPointName]['user']}
                        style={{
                          translate: activePart.controlPoints[controlPointName].toCSSTranslate(),
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setState((prev) => ({ ...prev, draggedControlPoint: controlPointName as any }));
                        }}
                      >
                        {/* We position the indicators on the top left and use CSS `translate` to move them to the appropriate place in order to bypass React's rendering, as it was unusably slow */}
                        <circle cx={-1} cy={-1} r={MARKER_EDITING_CIRCLE_RADIUS} stroke="blue" fill="white" strokeWidth={CONTROL_POINT_INDICATOR_STROKE_WIDTH} />
                      </g>
                      <g
                        ref={activeControlPointIndicatorRefs[controlPointName]['constrained']}
                        style={{
                          pointerEvents: 'none',
                          translate: activePart.controlPoints[controlPointName].toCSSTranslate(),
                        }}
                      >
                        <line x1={-1 - MARKER_EDITING_CROSS_RADIUS} y1={-1} x2={-1 + MARKER_EDITING_CROSS_RADIUS} y2={-1} stroke="green" strokeWidth={CONTROL_POINT_INDICATOR_STROKE_WIDTH} />
                        <line x1={-1} y1={-1 - MARKER_EDITING_CROSS_RADIUS} x2={-1} y2={-1 + MARKER_EDITING_CROSS_RADIUS} stroke="green" strokeWidth={CONTROL_POINT_INDICATOR_STROKE_WIDTH} />
                      </g>
                    </g>
                  ))}
              </svg>
            )}
          </section>
          <hr className="vr" />
          <aside>
            {(Object.keys(markers) as StepsType[])
              .map((step) => {
                const Configuration = markerConfigurationComponents[step][state.active[step] as string];
                const disabled = Object.entries(state.active).some(([activeStep, activeOption]) => (disabledStepCombinations as any)[activeStep]?.[activeOption]?.includes(step));
                return (
                  <details
                    key={step}
                    name="marker-editor-step"
                    className={cx({ disabled })}
                    title={disabled ? "This step is disabled because it's not compatible with some selected options" : undefined}
                    open={state.step === step}
                    onClick={(e) => {
                      if (disabled) e.preventDefault();
                    }}
                    onToggle={(e) => {
                      if (disabled && (e.nativeEvent as ToggleEvent).newState === 'open') {
                        e.currentTarget.open = false;
                      } else if ((e.nativeEvent as ToggleEvent).newState === 'open' && state.step !== step) {
                        setState((prev) => ({ ...prev, step }));
                      } else if ((e.nativeEvent as ToggleEvent).newState === 'closed' && state.step === step) {
                        e.currentTarget.open = true;
                      }
                    }}
                  >
                    <summary>{_startCase(step)}</summary>
                    <ul role="listbox">
                      {Object.entries(markers[step])
                        .map(([type, part]) => {
                          const Thumbnail = markerThumbnailComponents[step][type];
                          return (
                            <li
                              key={type}
                              role="option"
                              aria-selected={state.active[step] === type}
                              onClick={() => setState((prev) => ({ ...prev, active: { ...prev.active, [step]: type as any } }))}
                              title={part.title}
                            >
                              <Thumbnail />
                            </li>
                          );
                        })
                      }
                    </ul>

                    <section className="marker-step-configuration">
                      <Configuration />
                    </section>
                  </details>
                );
              }
              )}
          </aside>
        </article>
      </div>
    </dialog>
  );
}
